/**
 * Quality Manager Component
 *
 * Comprehensive quality management with grades, certifications,
 * and inspection requirements for inventory items
 */

import {
  CalendarIcon,
  CertificateIcon,
  ClockIcon,
  PlusIcon,
  StarIcon,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useToast } from '@/hooks/use-toast';
import { DataTableColumn } from '@/types/datatable';
import { InventoryItem, QualityInfo } from '@/types/inventory';
import { formatDate } from '@/utils';

interface QualityManagerProps {
  inventoryItem: InventoryItem;
  onUpdate?: () => void;
  className?: string;
}

interface Certification {
  id: string;
  name: string;
  type:
    | 'organic'
    | 'halal'
    | 'kosher'
    | 'fair_trade'
    | 'iso'
    | 'haccp'
    | 'other';
  issuedBy: string;
  issuedDate: string;
  expiryDate: string;
  certificateNumber: string;
  status: 'valid' | 'expired' | 'pending' | 'revoked';
  documentUrl?: string;
}

interface QualityInspection {
  id: string;
  inspectionDate: string;
  inspectorName: string;
  inspectionType: 'incoming' | 'routine' | 'customer_complaint' | 'audit';
  grade: QualityInfo['grade'];
  score: number; // 0-100
  notes: string;
  defects: string[];
  actionRequired: boolean;
  followUpDate?: string;
  status: 'passed' | 'failed' | 'conditional' | 'pending';
}

interface CertificationFormData {
  name: string;
  type: Certification['type'];
  issuedBy: string;
  issuedDate: string;
  expiryDate: string;
  certificateNumber: string;
  documentUrl?: string;
}

interface InspectionFormData {
  inspectionDate: string;
  inspectorName: string;
  inspectionType: QualityInspection['inspectionType'];
  grade: QualityInfo['grade'];
  score: number;
  notes: string;
  defects: string;
  actionRequired: boolean;
  followUpDate?: string;
}

const QUALITY_GRADES = [
  {
    value: 'Premium',
    label: 'Premium',
    color: 'bg-purple-100 text-purple-800',
    score: 95,
  },
  {
    value: 'A',
    label: 'Grade A',
    color: 'bg-green-100 text-green-800',
    score: 85,
  },
  {
    value: 'B',
    label: 'Grade B',
    color: 'bg-yellow-100 text-yellow-800',
    score: 75,
  },
  {
    value: 'C',
    label: 'Grade C',
    color: 'bg-orange-100 text-orange-800',
    score: 65,
  },
  {
    value: 'Rejected',
    label: 'Rejected',
    color: 'bg-red-100 text-red-800',
    score: 0,
  },
];

const CERTIFICATION_TYPES = [
  { value: 'organic', label: 'Organic Certification' },
  { value: 'halal', label: 'Halal Certification' },
  { value: 'kosher', label: 'Kosher Certification' },
  { value: 'fair_trade', label: 'Fair Trade' },
  { value: 'iso', label: 'ISO Standards' },
  { value: 'haccp', label: 'HACCP Certification' },
  { value: 'other', label: 'Other' },
];

const INSPECTION_TYPES = [
  { value: 'incoming', label: 'Incoming Inspection' },
  { value: 'routine', label: 'Routine Quality Check' },
  { value: 'customer_complaint', label: 'Customer Complaint' },
  { value: 'audit', label: 'Quality Audit' },
];

const COMMON_DEFECTS = [
  'Physical damage',
  'Color variation',
  'Size inconsistency',
  'Contamination',
  'Moisture content',
  'Packaging issues',
  'Foreign objects',
  'Texture problems',
  'Taste/odor issues',
  'Labeling errors',
];

export const QualityManager: React.FC<QualityManagerProps> = ({
  inventoryItem,
  onUpdate,
  className = '',
}) => {
  const { inventory: inventoryApi } = useEnhancedApi();
  const { toast } = useToast();
  const showToast = (message: string, success: boolean = true) => {
    toast({
      title: success ? 'Success' : 'Error',
      description: message,
      variant: success ? 'default' : 'destructive',
    });
  };
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [inspections, setInspections] = useState<QualityInspection[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeSheet, setActiveSheet] = useState<
    'certification' | 'inspection' | null
  >(null);
  const [editingCertification, setEditingCertification] =
    useState<Certification | null>(null);
  const [editingInspection, setEditingInspection] =
    useState<QualityInspection | null>(null);

  // Form states
  const [certificationForm, setCertificationForm] =
    useState<CertificationFormData>({
      name: '',
      type: 'organic',
      issuedBy: '',
      issuedDate: '',
      expiryDate: '',
      certificateNumber: '',
      documentUrl: '',
    });

  const [inspectionForm, setInspectionForm] = useState<InspectionFormData>({
    inspectionDate: new Date().toISOString().split('T')[0],
    inspectorName: '',
    inspectionType: 'incoming',
    grade: 'A',
    score: 85,
    notes: '',
    defects: '',
    actionRequired: false,
    followUpDate: '',
  });

  // Calculate quality statistics
  const qualityStats = useMemo(() => {
    const validCertifications = certifications.filter(
      c => c.status === 'valid'
    ).length;
    const expiringSoon = certifications.filter(c => {
      const expiryDate = new Date(c.expiryDate);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      return expiryDate <= thirtyDaysFromNow && c.status === 'valid';
    }).length;

    const recentInspections = inspections.filter(i => {
      const inspectionDate = new Date(i.inspectionDate);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return inspectionDate >= thirtyDaysAgo;
    });

    const averageScore =
      inspections.length > 0
        ? inspections.reduce((sum, i) => sum + i.score, 0) / inspections.length
        : 0;

    const passedInspections = inspections.filter(
      i => i.status === 'passed'
    ).length;
    const passRate =
      inspections.length > 0
        ? (passedInspections / inspections.length) * 100
        : 0;

    return {
      totalCertifications: certifications.length,
      validCertifications,
      expiringSoon,
      totalInspections: inspections.length,
      recentInspections: recentInspections.length,
      averageScore: Math.round(averageScore * 10) / 10,
      passRate: Math.round(passRate * 10) / 10,
      currentGrade: inventoryItem.quality?.grade || 'Not Set',
    };
  }, [certifications, inspections, inventoryItem.quality]);

  // Certification table columns
  const certificationColumns: DataTableColumn<Certification>[] = [
    {
      id: 'name',
      header: 'Certification',
      accessor: 'name',
      cell: ({ row }) => (
        <div className='font-medium'>
          <div className='flex items-center gap-2'>
            <CertificateIcon className='h-4 w-4 text-blue-500' />
            {(row as any).name}
          </div>
          <div className='text-xs text-gray-500 mt-1'>
            {
              CERTIFICATION_TYPES.find(t => t.value === (row as any).type)
                ?.label
            }
          </div>
        </div>
      ),
    },
    {
      id: 'issuedBy',
      header: 'Issued By',
      accessor: 'issuedBy',
      cell: ({ row }) => (
        <span className='text-gray-900'>{(row as any).issuedBy}</span>
      ),
    },
    {
      id: 'certificateNumber',
      header: 'Certificate #',
      accessor: 'certificateNumber',
      cell: ({ row }) => (
        <span className='font-mono text-sm'>
          {(row as any).certificateNumber}
        </span>
      ),
    },
    {
      id: 'expiryDate',
      header: 'Expires',
      accessor: 'expiryDate',
      cell: ({ row }) => {
        const expiryDate = new Date((row as any).expiryDate);
        const isExpiringSoon =
          expiryDate <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
        const isExpired = expiryDate < new Date();

        return (
          <div className='flex items-center gap-2'>
            <CalendarIcon className='h-4 w-4 text-gray-500' />
            <span
              className={
                isExpired
                  ? 'text-red-600 font-medium'
                  : isExpiringSoon
                    ? 'text-orange-600 font-medium'
                    : ''
              }
            >
              {formatDate((row as any).expiryDate)}
            </span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      cell: ({ row }) => {
        const statusColors = {
          valid: 'bg-green-100 text-green-800',
          expired: 'bg-red-100 text-red-800',
          pending: 'bg-yellow-100 text-yellow-800',
          revoked: 'bg-gray-100 text-gray-800',
        };
        return (
          <Badge
            className={
              statusColors[(row as any).status as keyof typeof statusColors]
            }
          >
            {(row as any).status.charAt(0).toUpperCase() +
              (row as any).status.slice(1)}
          </Badge>
        );
      },
    },
  ];

  // Inspection table columns
  const inspectionColumns: DataTableColumn<QualityInspection>[] = [
    {
      id: 'inspectionDate',
      header: 'Date',
      accessor: 'inspectionDate',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <CalendarIcon className='h-4 w-4 text-blue-500' />
          {formatDate((row as any).inspectionDate)}
        </div>
      ),
    },
    {
      id: 'inspector',
      header: 'Inspector',
      accessor: 'inspectorName',
      cell: ({ row }) => (
        <span className='font-medium'>{(row as any).inspectorName}</span>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      accessor: 'inspectionType',
      cell: ({ row }) => (
        <span className='text-sm'>
          {
            INSPECTION_TYPES.find(t => t.value === (row as any).inspectionType)
              ?.label
          }
        </span>
      ),
    },
    {
      id: 'grade',
      header: 'Grade',
      accessor: 'grade',
      cell: ({ row }) => {
        const grade = QUALITY_GRADES.find(g => g.value === (row as any).grade);
        return (
          <Badge className={grade?.color || 'bg-gray-100 text-gray-800'}>
            {grade?.label || (row as any).grade}
          </Badge>
        );
      },
    },
    {
      id: 'score',
      header: 'Score',
      accessor: 'score',
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <StarIcon className='h-4 w-4 text-yellow-500 fill-current' />
          <span className='font-medium'>{(row as any).score}/100</span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      cell: ({ row }) => {
        const statusColors = {
          passed: 'bg-green-100 text-green-800',
          failed: 'bg-red-100 text-red-800',
          conditional: 'bg-yellow-100 text-yellow-800',
          pending: 'bg-blue-100 text-blue-800',
        };
        return (
          <Badge
            className={
              statusColors[(row as any).status as keyof typeof statusColors]
            }
          >
            {(row as any).status.charAt(0).toUpperCase() +
              (row as any).status.slice(1)}
          </Badge>
        );
      },
    },
  ];

  // Reset certification form
  const resetCertificationForm = () => {
    setCertificationForm({
      name: '',
      type: 'organic',
      issuedBy: '',
      issuedDate: '',
      expiryDate: '',
      certificateNumber: '',
      documentUrl: '',
    });
    setEditingCertification(null);
  };

  // Reset inspection form
  const resetInspectionForm = () => {
    setInspectionForm({
      inspectionDate: new Date().toISOString().split('T')[0],
      inspectorName: '',
      inspectionType: 'incoming',
      grade: 'A',
      score: 85,
      notes: '',
      defects: '',
      actionRequired: false,
      followUpDate: '',
    });
    setEditingInspection(null);
  };

  // Handle certification submission
  const handleCertificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const certificationData: Certification = {
        id: editingCertification?.id || `cert-${Date.now()}`,
        ...certificationForm,
        status: 'valid', // Default to valid, can be changed later
      };

      if (editingCertification) {
        // Update existing certification
        const updatedCertifications = certifications.map(c =>
          c.id === editingCertification.id ? certificationData : c
        );
        setCertifications(updatedCertifications);
        showToast('Certification updated successfully', true);
      } else {
        // Add new certification
        setCertifications([...certifications, certificationData]);
        showToast('Certification added successfully', true);
      }

      resetCertificationForm();
      setActiveSheet(null);
      onUpdate?.();
    } catch (error) {
      showToast('Failed to save certification', false);
    } finally {
      setLoading(false);
    }
  };

  // Handle inspection submission
  const handleInspectionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const inspectionData: QualityInspection = {
        id: editingInspection?.id || `insp-${Date.now()}`,
        ...inspectionForm,
        defects: inspectionForm.defects
          .split(',')
          .map(d => d.trim())
          .filter(d => d),
        status:
          inspectionForm.score >= 80
            ? 'passed'
            : inspectionForm.score >= 60
              ? 'conditional'
              : 'failed',
      };

      if (editingInspection) {
        // Update existing inspection
        const updatedInspections = inspections.map(i =>
          i.id === editingInspection.id ? inspectionData : i
        );
        setInspections(updatedInspections);
        showToast('Inspection updated successfully', true);
      } else {
        // Add new inspection
        setInspections([...inspections, inspectionData]);
        showToast('Inspection recorded successfully', true);
      }

      // Update inventory item quality grade if this is the latest inspection
      if (
        inspectionData.inspectionDate >= (inspections[0]?.inspectionDate || '')
      ) {
        await inventoryApi.updateQualityInfo(inventoryItem._id!, {
          grade: inspectionData.grade,
          lastInspectionDate: inspectionData.inspectionDate,
          lastInspectionScore: inspectionData.score,
        });
      }

      resetInspectionForm();
      setActiveSheet(null);
      onUpdate?.();
    } catch (error) {
      showToast('Failed to save inspection', false);
    } finally {
      setLoading(false);
    }
  };

  // Row actions
  const certificationRowActions = (row: Certification): any[] => [
    {
      label: 'Edit Certification',
      onClick: () => {
        setCertificationForm({
          name: row.name,
          type: row.type,
          issuedBy: row.issuedBy,
          issuedDate: row.issuedDate,
          expiryDate: row.expiryDate,
          certificateNumber: row.certificateNumber,
          documentUrl: row.documentUrl,
        });
        setEditingCertification(row);
        setActiveSheet('certification');
      },
    },
    {
      label: 'View Document',
      onClick: () => {
        if (row.documentUrl) {
          window.open(row.documentUrl, '_blank');
        } else {
          showToast('No document URL available', false);
        }
      },
      isVisible: !!row.documentUrl,
    },
    {
      label: 'Delete Certification',
      onClick: () => {
        if (confirm('Are you sure you want to delete this certification?')) {
          setCertifications(certifications.filter(c => c.id !== row.id));
          showToast('Certification deleted', true);
        }
      },
      className: 'text-red-600 hover:text-red-700',
    },
  ];

  const inspectionRowActions = (row: QualityInspection): any[] => [
    {
      label: 'Edit Inspection',
      onClick: () => {
        setInspectionForm({
          inspectionDate: row.inspectionDate,
          inspectorName: row.inspectorName,
          inspectionType: row.inspectionType,
          grade: row.grade,
          score: row.score,
          notes: row.notes,
          defects: row.defects.join(', '),
          actionRequired: row.actionRequired,
          followUpDate: row.followUpDate,
        });
        setEditingInspection(row);
        setActiveSheet('inspection');
      },
    },
    {
      label: 'Delete Inspection',
      onClick: () => {
        if (
          confirm('Are you sure you want to delete this inspection record?')
        ) {
          setInspections(inspections.filter(i => i.id !== row.id));
          showToast('Inspection record deleted', true);
        }
      },
      className: 'text-red-600 hover:text-red-700',
    },
  ];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Quality Statistics */}
      <div className='grid grid-cols-1 md:grid-cols-8 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Current Grade
            </div>
            <div className='text-2xl font-bold text-purple-600'>
              {qualityStats.currentGrade}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Certifications
            </div>
            <div className='text-2xl font-bold'>
              {qualityStats.totalCertifications}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Valid Certs</div>
            <div className='text-2xl font-bold text-green-600'>
              {qualityStats.validCertifications}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Expiring Soon
            </div>
            <div className='text-2xl font-bold text-orange-600'>
              {qualityStats.expiringSoon}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Inspections</div>
            <div className='text-2xl font-bold'>
              {qualityStats.totalInspections}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Recent</div>
            <div className='text-2xl font-bold text-blue-600'>
              {qualityStats.recentInspections}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Avg Score</div>
            <div className='text-2xl font-bold text-yellow-600'>
              {qualityStats.averageScore}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Pass Rate</div>
            <div className='text-2xl font-bold text-green-600'>
              {qualityStats.passRate}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quality Alerts */}
      {qualityStats.expiringSoon > 0 && (
        <Alert className='border-orange-200 bg-orange-50'>
          <ClockIcon className='h-4 w-4 text-orange-600' />
          <AlertDescription>
            <strong>
              {qualityStats.expiringSoon} certification
              {qualityStats.expiringSoon !== 1 ? 's' : ''}
            </strong>{' '}
            expiring within 30 days require renewal.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue='certifications'>
        <TabsList>
          <TabsTrigger value='certifications'>Certifications</TabsTrigger>
          <TabsTrigger value='inspections'>Quality Inspections</TabsTrigger>
        </TabsList>

        <TabsContent value='certifications'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4'>
              <CardTitle className='text-lg font-semibold'>
                Quality Certifications
              </CardTitle>
              <Sheet
                open={activeSheet === 'certification'}
                onOpenChange={open =>
                  setActiveSheet(open ? 'certification' : null)
                }
              >
                <SheetTrigger asChild>
                  <Button onClick={resetCertificationForm}>
                    <PlusIcon className='h-4 w-4 mr-2' />
                    Add Certification
                  </Button>
                </SheetTrigger>
                <SheetContent className='w-full sm:max-w-lg'>
                  <SheetHeader>
                    <SheetTitle>
                      {editingCertification
                        ? 'Edit Certification'
                        : 'Add New Certification'}
                    </SheetTitle>
                  </SheetHeader>

                  <form
                    onSubmit={handleCertificationSubmit}
                    className='space-y-4 mt-6'
                  >
                    <div className='space-y-2'>
                      <Label htmlFor='certName'>Certification Name *</Label>
                      <Input
                        id='certName'
                        value={certificationForm.name}
                        onChange={e =>
                          setCertificationForm(prev => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder='e.g., USDA Organic'
                        required
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='certType'>Type *</Label>
                      <Select
                        value={certificationForm.type}
                        onValueChange={value =>
                          setCertificationForm(prev => ({
                            ...prev,
                            type: value as any,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CERTIFICATION_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='issuedBy'>Issued By *</Label>
                      <Input
                        id='issuedBy'
                        value={certificationForm.issuedBy}
                        onChange={e =>
                          setCertificationForm(prev => ({
                            ...prev,
                            issuedBy: e.target.value,
                          }))
                        }
                        placeholder='e.g., USDA'
                        required
                      />
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='issuedDate'>Issue Date *</Label>
                        <Input
                          id='issuedDate'
                          type='date'
                          value={certificationForm.issuedDate}
                          onChange={e =>
                            setCertificationForm(prev => ({
                              ...prev,
                              issuedDate: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label htmlFor='expiryDate'>Expiry Date *</Label>
                        <Input
                          id='expiryDate'
                          type='date'
                          value={certificationForm.expiryDate}
                          onChange={e =>
                            setCertificationForm(prev => ({
                              ...prev,
                              expiryDate: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='certNumber'>Certificate Number *</Label>
                      <Input
                        id='certNumber'
                        value={certificationForm.certificateNumber}
                        onChange={e =>
                          setCertificationForm(prev => ({
                            ...prev,
                            certificateNumber: e.target.value,
                          }))
                        }
                        placeholder='Certificate number'
                        required
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='documentUrl'>Document URL</Label>
                      <Input
                        id='documentUrl'
                        value={certificationForm.documentUrl}
                        onChange={e =>
                          setCertificationForm(prev => ({
                            ...prev,
                            documentUrl: e.target.value,
                          }))
                        }
                        placeholder='https://example.com/certificate.pdf'
                      />
                    </div>

                    <Separator />

                    <div className='flex justify-end gap-2'>
                      <Button
                        type='button'
                        hierarchy='outlined'
                        onClick={() => setActiveSheet(null)}
                      >
                        Cancel
                      </Button>
                      <Button type='submit' loading={loading}>
                        {editingCertification
                          ? 'Update Certification'
                          : 'Add Certification'}
                      </Button>
                    </div>
                  </form>
                </SheetContent>
              </Sheet>
            </CardHeader>
            <CardContent>
              <DataTable
                data={certifications}
                columns={certificationColumns}
                rowActions={certificationRowActions as any}
                emptyConfig={{
                  title: 'No Certifications Found',
                  description:
                    'Add quality certifications to demonstrate product standards.',
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='inspections'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4'>
              <CardTitle className='text-lg font-semibold'>
                Quality Inspections
              </CardTitle>
              <Sheet
                open={activeSheet === 'inspection'}
                onOpenChange={open =>
                  setActiveSheet(open ? 'inspection' : null)
                }
              >
                <SheetTrigger asChild>
                  <Button onClick={resetInspectionForm}>
                    <PlusIcon className='h-4 w-4 mr-2' />
                    Record Inspection
                  </Button>
                </SheetTrigger>
                <SheetContent className='w-full sm:max-w-lg'>
                  <SheetHeader>
                    <SheetTitle>
                      {editingInspection
                        ? 'Edit Inspection'
                        : 'Record Quality Inspection'}
                    </SheetTitle>
                  </SheetHeader>

                  <form
                    onSubmit={handleInspectionSubmit}
                    className='space-y-4 mt-6'
                  >
                    <div className='grid grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='inspectionDate'>
                          Inspection Date *
                        </Label>
                        <Input
                          id='inspectionDate'
                          type='date'
                          value={inspectionForm.inspectionDate}
                          onChange={e =>
                            setInspectionForm(prev => ({
                              ...prev,
                              inspectionDate: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>
                      <div className='space-y-2'>
                        <Label htmlFor='inspector'>Inspector Name *</Label>
                        <Input
                          id='inspector'
                          value={inspectionForm.inspectorName}
                          onChange={e =>
                            setInspectionForm(prev => ({
                              ...prev,
                              inspectorName: e.target.value,
                            }))
                          }
                          placeholder='Inspector name'
                          required
                        />
                      </div>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='inspectionType'>Inspection Type *</Label>
                      <Select
                        value={inspectionForm.inspectionType}
                        onValueChange={value =>
                          setInspectionForm(prev => ({
                            ...prev,
                            inspectionType: value as any,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {INSPECTION_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='grid grid-cols-2 gap-4'>
                      <div className='space-y-2'>
                        <Label htmlFor='grade'>Quality Grade *</Label>
                        <Select
                          value={inspectionForm.grade}
                          onValueChange={value =>
                            setInspectionForm(prev => ({
                              ...prev,
                              grade: value as any,
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {QUALITY_GRADES.map(grade => (
                              <SelectItem key={grade.value} value={grade.value}>
                                {grade.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className='space-y-2'>
                        <Label htmlFor='score'>Score (0-100) *</Label>
                        <Input
                          id='score'
                          type='number'
                          value={inspectionForm.score}
                          onChange={e =>
                            setInspectionForm(prev => ({
                              ...prev,
                              score: parseInt(e.target.value) || 0,
                            }))
                          }
                          min='0'
                          max='100'
                          required
                        />
                      </div>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='defects'>
                        Defects Found (comma-separated)
                      </Label>
                      <Input
                        id='defects'
                        value={inspectionForm.defects}
                        onChange={e =>
                          setInspectionForm(prev => ({
                            ...prev,
                            defects: e.target.value,
                          }))
                        }
                        placeholder='e.g., Color variation, Size inconsistency'
                      />
                      <div className='text-xs text-gray-500'>
                        Common defects: {COMMON_DEFECTS.slice(0, 3).join(', ')},
                        etc.
                      </div>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='notes'>Inspection Notes *</Label>
                      <Textarea
                        id='notes'
                        value={inspectionForm.notes}
                        onChange={e =>
                          setInspectionForm(prev => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        placeholder='Detailed inspection findings...'
                        rows={3}
                        required
                      />
                    </div>

                    <div className='flex items-center justify-between'>
                      <div className='flex items-center space-x-2'>
                        <Switch
                          id='actionRequired'
                          checked={inspectionForm.actionRequired}
                          onCheckedChange={checked =>
                            setInspectionForm(prev => ({
                              ...prev,
                              actionRequired: checked,
                            }))
                          }
                        />
                        <Label htmlFor='actionRequired'>Action Required</Label>
                      </div>
                    </div>

                    {inspectionForm.actionRequired && (
                      <div className='space-y-2'>
                        <Label htmlFor='followUpDate'>Follow-up Date</Label>
                        <Input
                          id='followUpDate'
                          type='date'
                          value={inspectionForm.followUpDate}
                          onChange={e =>
                            setInspectionForm(prev => ({
                              ...prev,
                              followUpDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                    )}

                    <Separator />

                    <div className='flex justify-end gap-2'>
                      <Button
                        type='button'
                        hierarchy='outlined'
                        onClick={() => setActiveSheet(null)}
                      >
                        Cancel
                      </Button>
                      <Button type='submit' loading={loading}>
                        {editingInspection
                          ? 'Update Inspection'
                          : 'Record Inspection'}
                      </Button>
                    </div>
                  </form>
                </SheetContent>
              </Sheet>
            </CardHeader>
            <CardContent>
              <DataTable
                data={inspections}
                columns={inspectionColumns}
                rowActions={inspectionRowActions as any}
                emptyConfig={{
                  title: 'No Inspections Recorded',
                  description:
                    'Record quality inspections to track product standards.',
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
